<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { confirmState, settleConfirm } from "@/composables/useConfirm";
import { t } from "@/copy/strings";

const reason = ref("");
const typed = ref("");

const opts = computed(() => confirmState.options);
const reasonCfg = computed(() => {
  const r = opts.value?.reason;
  if (!r) return null;
  return { label: t.common.reason, placeholder: t.common.reasonHint, minLength: 3, ...(typeof r === "object" ? r : {}) };
});
const valid = computed(() => {
  if (reasonCfg.value && reason.value.trim().length < reasonCfg.value.minLength) return false;
  if (opts.value?.typeToConfirm && typed.value !== opts.value.typeToConfirm) return false;
  return true;
});

watch(
  () => confirmState.open,
  (open) => {
    if (open) {
      reason.value = "";
      typed.value = "";
    }
  },
);

function submit() {
  if (valid.value) settleConfirm({ reason: reason.value.trim() });
}
</script>

<template>
  <Dialog :open="confirmState.open" @update:open="(v) => !v && settleConfirm(null)">
    <DialogContent v-if="opts" class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{{ opts.title }}</DialogTitle>
        <DialogDescription v-if="opts.description">{{ opts.description }}</DialogDescription>
      </DialogHeader>
      <form class="grid gap-4" @submit.prevent="submit">
        <div v-if="reasonCfg" class="grid gap-1.5">
          <Label for="confirm-reason">{{ reasonCfg.label }}</Label>
          <Textarea id="confirm-reason" v-model="reason" rows="2" :placeholder="reasonCfg.placeholder" autofocus />
        </div>
        <div v-if="opts.typeToConfirm" class="grid gap-1.5">
          <Label for="confirm-typed">Type <code class="rounded bg-muted px-1 font-semibold">{{ opts.typeToConfirm }}</code> to confirm</Label>
          <Input id="confirm-typed" v-model="typed" autocomplete="off" spellcheck="false" />
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" @click="settleConfirm(null)">{{ t.common.cancel }}</Button>
          <Button type="submit" :variant="opts.destructive ? 'destructive' : 'default'" :disabled="!valid">
            {{ opts.confirmLabel ?? t.common.confirm }}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>
